import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AddToCalendarMenu from "@/components/calendar/AddToCalendarMenu";
import DateOfBirthInput from "@/components/DateOfBirthInput";
import { BoneIcon as Bone, PawPrintIcon as PawPrint, DropIcon as Drop, ConfettiIcon as Confetti, LightningIcon as Lightning, CalendarBlankIcon as CalendarBlank, FilePdfIcon as FilePdf, ShareIcon as Share, TrashIcon as Trash, PlusIcon as Plus, SparkleIcon as Sparkle, HeartIcon as Heart, LockIcon as Lock, CopyIcon as Copy, ScalesIcon as Scales, PillIcon as Pill, StethoscopeIcon as Stethoscope, GraduationCapIcon as GraduationCap, ShowerIcon as Shower, CameraIcon as Camera, WarningIcon as Warning, QrCodeIcon as QrCode, PaperPlaneTiltIcon as PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import useIsUsAccount from "@/hooks/useIsUsAccount";
import { ArrowSquareOutIcon as ArrowSquareOut } from "@phosphor-icons/react";
import { formatApiError, formatDate, thumbUrl, photoUrl as resolveFileUrl } from "@/lib/utils-app";

const KIND_META = {
  meal: { label: "Fed", icon: Bone, color: "#C9A227" },
  walk: { label: "Walk", icon: PawPrint, color: "#3F4A8A" },
  potty: { label: "Potty", icon: Drop, color: "#0F8043" },
  play: { label: "Play", icon: Confetti, color: "#C1272D" },
  energy: { label: "Energy", icon: Lightning, color: "#8A6A00" },
  weight: { label: "Weight", icon: Scales, color: "#0E0E0E" },
  meds: { label: "Meds", icon: Pill, color: "#7E22CE" },
  symptom: { label: "Symptom", icon: Stethoscope, color: "#C1272D" },
  training: { label: "Training", icon: GraduationCap, color: "#0F8043" },
  grooming: { label: "Grooming", icon: Shower, color: "#3F4A8A" },
};

/** Owner-only Dog Log Care Hub for a single dog. Paywall-gates on
 *  the Dog Log subscription (auto-bundled with Breeder Pro / Kennel Elite). */
export default function CareTab({ dog, isOwnerOrAdmin }) {
  const [status, setStatus] = useState(null); // { active, packages }
  const [log, setLog] = useState([]);
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [sitterLinks, setSitterLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Hydrate everything in parallel once we know we're allowed.
  const refresh = useCallback(async () => {
    try {
      const s = await api.get("/care/status");
      setStatus(s.data);
      if (s.data.active) {
        const [l, st, a, sl] = await Promise.all([
          api.get(`/care/log?dog_id=${dog.id}&days=30`),
          api.get(`/care/stats?dog_id=${dog.id}`),
          api.get(`/care/appointments?dog_id=${dog.id}&upcoming_only=true`),
          api.get(`/care/sitter-links?dog_id=${dog.id}`),
        ]);
        setLog(l.data);
        setStats(st.data);
        setAppointments(a.data);
        setSitterLinks(sl.data);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [dog.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!isOwnerOrAdmin) {
    return (
      <div className="rounded-xl border border-[#E5E5E5] bg-white p-6 text-center text-sm text-[#6B6B6B]" data-testid="care-not-owner">
        The Dog Log is only visible to the dog's owner.
      </div>
    );
  }
  if (loading) return <div className="p-6 text-sm text-[#6B6B6B]">Loading Dog Log…</div>;
  if (!status?.active) {
    return isNativeApp() ? <CarePaywallNative /> : <CarePaywall packages={status?.packages} />;
  }

  return (
    <div className="space-y-6" data-testid="care-tab">
      {stats?.consider_vet && (
        <div
          className="rounded-xl border border-crimson/40 bg-[#FEF1F1] p-4 flex items-start gap-3"
          data-testid="care-vet-warning"
        >
          <Warning size={20} weight="duotone" className="text-crimson flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm text-crimson">
              Consider a vet visit
            </p>
            <p className="text-[12.5px] text-[#6B6B6B] leading-snug mt-0.5">
              {dog.name} has had moderate-to-severe symptoms logged for {stats.consider_vet_run_days || 2} days in a row.
              Tap "Add" under Appointments to schedule a check.
            </p>
          </div>
        </div>
      )}
      <DailyLog dogId={dog.id} log={log} onChange={refresh} />
      <StatsPanel stats={stats} />
      <AppointmentsPanel dogId={dog.id} appointments={appointments} onChange={refresh} />
      <SitterLinksPanel dogId={dog.id} dog={dog} links={sitterLinks} onChange={refresh} />
      <ReportsPanel dogId={dog.id} sitterLinks={sitterLinks} />
      <RecapPreferencesCard />
    </div>
  );
}

// ----------------------------------------------------------------------
// Paywall card — shown when status.active === false
// ----------------------------------------------------------------------
function CarePaywall({ packages }) {
  const [submitting, setSubmitting] = useState(null);
  const start = async (packageId) => {
    setSubmitting(packageId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: webOrigin(),
      });
      if (data.url) window.location.href = data.url;
      else if (data.free) {
        toast.success("Dog Log unlocked — refreshing…");
        window.location.reload();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(null);
    }
  };
  if (isIOS()) {
    return (
      <div className="rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC] p-8" data-testid="care-paywall">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2 flex items-center gap-1.5">
          <Lock size={11} weight="fill" /> Dog Log subscription
        </p>
        <h2 className="font-spectral text-3xl tracking-tight leading-tight mb-3">
          The complete diary your dog deserves — and your vet will love.
        </h2>
        <p className="text-[14px] text-[#3F3F3F] leading-relaxed">
          Dog Log is included with a subscription.
        </p>
        <a
          href="/account/subscription"
          className="mt-5 inline-flex items-center rounded-full bg-ink hover:bg-crimson text-white text-[13px] font-semibold px-5 py-2.5 transition-colors"
          data-testid="care-paywall-ios-plans"
        >
          View subscription options
        </a>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC] p-8" data-testid="care-paywall">
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2 flex items-center gap-1.5">
        <Lock size={11} weight="fill" /> Dog Log subscription
      </p>
      <h2 className="font-spectral text-3xl tracking-tight leading-tight mb-3">
        The complete diary your dog deserves — and your vet will love.
      </h2>
      <p className="text-[#3F3F3F] mb-5 max-w-xl leading-relaxed">
        Dog Log gives you a one-tap diary across 10 categories — meals, walks,
        potty, play, energy, weight, meds, symptoms, training, and grooming —
        plus streaks, smart vet-warning alerts, vet-ready PDF reports, and
        shareable sitter access that takes seconds to set up.
      </p>
      <ul className="space-y-2 text-sm text-[#3F3F3F] mb-6 max-w-xl">
        <li className="flex items-start gap-2"><Sparkle size={12} weight="fill" className="text-gold mt-1.5 flex-shrink-0" /> 10 log categories with rich detail — every entry can include a photo</li>
        <li className="flex items-start gap-2"><Sparkle size={12} weight="fill" className="text-gold mt-1.5 flex-shrink-0" /> Streaks · weight trends · vet-warning alerts when symptoms repeat</li>
        <li className="flex items-start gap-2"><Sparkle size={12} weight="fill" className="text-gold mt-1.5 flex-shrink-0" /> Appointments with iCal export (Google Calendar &amp; Apple Calendar)</li>
        <li className="flex items-start gap-2"><Sparkle size={12} weight="fill" className="text-gold mt-1.5 flex-shrink-0" /> 3 downloadable PDFs: Vet Visit · Sitter Briefing · Sitter Return Report</li>
        <li className="flex items-start gap-2"><Sparkle size={12} weight="fill" className="text-gold mt-1.5 flex-shrink-0" /> Sitter sharing — send by email, QR code, or one-tap native share</li>
        <li className="flex items-start gap-2"><Sparkle size={12} weight="fill" className="text-gold mt-1.5 flex-shrink-0" /> Saturday weekly recap email · unlimited dogs · auto-included with Breeder Pro / Elite</li>
      </ul>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
        <Button
          onClick={() => start("care_plus_monthly")}
          disabled={!!submitting}
          variant="outline"
          className="h-auto py-4 rounded-xl border-ink hover:bg-ink hover:text-white"
          data-testid="care-paywall-monthly"
        >
          <div className="flex flex-col items-start text-left w-full">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">Monthly</span>
            <span className="font-spectral text-2xl font-light mt-1">${packages?.monthly.amount?.toFixed(2)}<span className="text-sm opacity-70">/mo</span></span>
          </div>
        </Button>
        <Button
          onClick={() => start("care_plus_annual")}
          disabled={!!submitting}
          className="h-auto py-4 rounded-xl bg-ink hover:bg-crimson text-white"
          data-testid="care-paywall-annual"
        >
          <div className="flex flex-col items-start text-left w-full">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-gold">Annual · save 38%</span>
            <span className="font-spectral text-2xl font-light mt-1">${packages?.annual.amount?.toFixed(2)}<span className="text-sm opacity-70">/yr</span></span>
          </div>
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Native paywall — App Review 3.1.1: no in-app subscription pricing.
// US accounts get the compliant web link-out; others see support info.
// ----------------------------------------------------------------------
function CarePaywallNative() {
  const isUS = useIsUsAccount();
  return (
    <div className="rounded-2xl border border-[#E8D9A6] bg-[#FBF7EC] p-8" data-testid="care-paywall-native">
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2 flex items-center gap-1.5">
        <Lock size={11} weight="fill" /> Dog Log
      </p>
      <h2 className="font-spectral text-3xl tracking-tight leading-tight mb-3">
        The complete diary your dog deserves — and your vet will love.
      </h2>
      <p className="text-[#3F3F3F] mb-5 max-w-xl leading-relaxed">
        Dog Log gives you a one-tap diary across 10 categories — meals, walks,
        potty, play, energy, weight, meds, symptoms, training, and grooming —
        plus streaks, smart vet-warning alerts, vet-ready PDF reports, and
        shareable sitter access. It isn&rsquo;t active on your account yet.
      </p>
      {isIOS() ? (
        <Link
          to="/account/subscription"
          className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-crimson text-white text-[13px] font-semibold px-5 py-3 transition-colors"
          data-testid="care-paywall-native-link"
        >
          View subscription options
        </Link>
      ) : isUS ? (
        <a
          href="https://armstrongk9registry.com/dog-log/about"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-crimson text-white text-[13px] font-semibold px-5 py-3 transition-colors"
          data-testid="care-paywall-native-link"
        >
          Activate Dog Log on armstrongk9registry.com
          <ArrowSquareOut size={15} weight="bold" />
        </a>
      ) : (
        <p className="text-sm text-[#6B6B6B]">
          Contact <span className="font-semibold text-ink">hello@armstrongk9registry.com</span> to
          activate Dog Log on your account.
        </p>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Daily log — quick-tap buttons + today's tape
// ----------------------------------------------------------------------
function DailyLog({ dogId, log, onChange }) {
  const [activeKind, setActiveKind] = useState(null);
  const [instantBusy, setInstantBusy] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const todays = log.filter((r) => (r.occurred_at || "").startsWith(today));

  // One-tap instant log — saves immediately with sensible defaults and
  // offers Undo / "Add details" via the toast. The full grid below still
  // opens the detailed dialog for anyone who wants specifics.
  const instantLog = async (kind) => {
    setInstantBusy(kind);
    try {
      const r = await api.post("/care/log", { dog_id: dogId, kind });
      const entryId = r.data?.id;
      onChange();
      toast.success(`${KIND_META[kind].label} logged ✓`, {
        description: "Tap a button below again anytime — or undo this one.",
        action: entryId
          ? {
              label: "Undo",
              onClick: async () => {
                try {
                  await api.delete(`/care/log/${entryId}`);
                  onChange();
                  toast.info("Entry removed.");
                } catch { /* already gone */ }
              },
            }
          : undefined,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't log that.");
    } finally {
      setInstantBusy(null);
    }
  };

  return (
    <section className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="care-daily-log">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-spectral text-2xl tracking-tight">Today</h3>
        <span className="text-[11px] text-[#6B6B6B]">
          {todays.length} {todays.length === 1 ? "entry" : "entries"} so far
        </span>
      </div>

      {/* One-tap row — the 5 most common kinds, logged instantly. */}
      <div className="mb-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold-dark mb-2">
          One-tap log
        </p>
        <div className="flex flex-wrap gap-2" data-testid="care-instant-row">
          {["walk", "meal", "potty", "meds", "play"].map((kind) => {
            const meta = KIND_META[kind];
            return (
              <button
                key={kind}
                type="button"
                onClick={() => instantLog(kind)}
                disabled={instantBusy === kind}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#EAEAEA] bg-[#FBFBFB] hover:bg-cream hover:border-gold/50 px-3.5 py-2 text-[12.5px] font-semibold text-ink active:scale-95 transition-all disabled:opacity-50"
                data-testid={`care-instant-${kind}`}
              >
                {React.createElement(meta.icon, { size: 15, weight: "duotone", style: { color: meta.color } })}
                {instantBusy === kind ? "Saving…" : meta.label}
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#9A9A9A] mb-2">
        Log with details
      </p>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="care-quick-buttons">
        {Object.keys(KIND_META).map((kind) => (
          <QuickLogBtn key={kind} kind={kind} onClick={() => setActiveKind(kind)} />
        ))}
      </div>

      {/* Today's activity tape */}
      {todays.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#EAEAEA] space-y-2">
          {todays.map((e) => (
            <LogRow key={e.id} entry={e} />
          ))}
        </div>
      )}

      <LogDetailDialog
        kind={activeKind}
        dogId={dogId}
        onClose={() => setActiveKind(null)}
        onSaved={() => { setActiveKind(null); onChange(); }}
      />
    </section>
  );
}

function LogRow({ entry }) {
  const meta = KIND_META[entry.kind] || KIND_META.meal;
  const Icon = meta.icon;
  const time = (entry.occurred_at || "").slice(11, 16);
  const bits = formatEntryBits(entry);
  return (
    <div className="flex items-start gap-3 text-sm" data-testid={`care-log-entry-${entry.id}`}>
      <span className="text-[10px] font-mono text-[#9A9A9A] w-12 flex-shrink-0 pt-0.5">{time}</span>
      <Icon size={14} weight="duotone" style={{ color: meta.color }} className="flex-shrink-0 mt-0.5" />
      <span className="font-medium w-16 flex-shrink-0">{meta.label}</span>
      <span className="text-[#6B6B6B] flex-1 truncate">{bits || "—"}</span>
      {entry.photo_url && (
        <a href={resolveFileUrl(entry.photo_url) || entry.photo_url} target="_blank" rel="noreferrer" className="text-[#9A9A9A] hover:text-ink flex-shrink-0">
          <Camera size={14} weight="duotone" />
        </a>
      )}
      {entry.logged_by === "sitter" && (
        <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-gold bg-gold/10 rounded-full px-1.5 py-0.5 flex-shrink-0">sitter</span>
      )}
    </div>
  );
}

/** Build the right-hand summary text for an entry from its
 *  kind-specific fields. Order matters — most specific first. */
function formatEntryBits(e) {
  const bits = [];
  const d = e.details || {};
  if (e.distance_mi) bits.push(`${e.distance_mi} mi`);
  if (e.duration_min) bits.push(`${e.duration_min} min`);
  if (e.energy_level) bits.push(`energy ${e.energy_level}/5`);
  if (e.severity) bits.push(`severity ${e.severity}/5`);
  if (e.weight_lbs) bits.push(`${e.weight_lbs} lbs`);
  if (d.food) bits.push(d.food);
  if (d.cups) bits.push(`${d.cups} cups`);
  if (d.slot) bits.push(d.slot);
  if (d.type) bits.push(d.type);
  if (d.activity) bits.push(d.activity);
  if (d.med_name) bits.push(d.med_name);
  if (d.dosage) bits.push(d.dosage);
  if (d.what) bits.push(d.what);
  if (d.task) bits.push(d.task);
  if (d.pace) bits.push(d.pace);
  if (d.weather) bits.push(d.weather);
  if (d.mood) bits.push(d.mood);
  if (e.notes) bits.push(e.notes);
  return bits.join(" · ");
}

function QuickLogBtn({ kind, onClick }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`care-quick-${kind}`}
      className="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-[#EAEAEA] bg-white hover:border-ink hover:-translate-y-0.5 transition-all"
    >
      <Icon size={24} weight="duotone" style={{ color: meta.color }} />
      <span className="text-xs font-semibold">{meta.label}</span>
    </button>
  );
}

// ----------------------------------------------------------------------
// LogDetailDialog — one dialog per kind with kind-specific fields.
// Each kind config below declares the input fields that get rendered
// dynamically; the form state is a single `values` object that maps
// 1:1 onto either top-level columns (distance_mi, energy_level, etc.)
// or into the flexible `details` JSON for everything kind-specific.
// ----------------------------------------------------------------------
const KIND_FIELDS = {
  meal: {
    title: "Log a meal",
    subtitle: "Capture what, when, and how much.",
    fields: [
      { key: "slot", label: "Meal", type: "chips", options: ["breakfast", "lunch", "dinner", "treat"], detail: true },
      { key: "food", label: "Food / brand", type: "text", detail: true, placeholder: "e.g. Acana Adult" },
      { key: "cups", label: "Cups", type: "number", step: "0.1", detail: true },
      { key: "appetite", label: "Appetite", type: "chips", options: ["great", "normal", "picky", "refused"], detail: true },
      { key: "water", label: "Water intake", type: "chips", options: ["low", "normal", "high"], detail: true },
      { key: "supplements", label: "Supplements", type: "text", detail: true, placeholder: "fish oil, joint, ..." },
    ],
  },
  walk: {
    title: "Log a walk",
    subtitle: "Distance, duration, and how it went.",
    fields: [
      { key: "distance_mi", label: "Distance (mi)", type: "number", step: "0.1", detail: false, half: true },
      { key: "duration_min", label: "Duration (min)", type: "number", detail: false, half: true },
      { key: "pace", label: "Pace", type: "chips", options: ["leisurely", "brisk", "run"], detail: true },
      { key: "weather", label: "Weather", type: "chips", options: ["sunny", "cloudy", "rainy", "snowy", "hot", "cold"], detail: true },
      { key: "pulled_on_leash", label: "Pulled on leash", type: "toggle", detail: true },
    ],
  },
  potty: {
    title: "Log a potty",
    subtitle: "Quick notes that can flag health concerns early.",
    fields: [
      { key: "type", label: "Type", type: "chips", options: ["pee", "poo", "both"], detail: true },
      { key: "consistency", label: "Consistency (1=loose, 5=firm)", type: "scale", detail: true },
      { key: "color", label: "Color", type: "chips", options: ["normal", "dark", "light", "unusual"], detail: true },
      { key: "accident", label: "Accident indoors", type: "toggle", detail: true },
    ],
  },
  play: {
    title: "Log a play session",
    subtitle: "Capture the activity and the vibe.",
    fields: [
      { key: "activity", label: "Activity", type: "chips", options: ["fetch", "tug", "wrestling", "puzzle", "training", "free"], detail: true },
      { key: "duration_min", label: "Duration (min)", type: "number", detail: false },
      { key: "location", label: "Where", type: "chips", options: ["indoor", "yard", "park", "other"], detail: true },
      { key: "played_with", label: "With", type: "chips", options: ["alone", "human", "other dog"], detail: true },
    ],
  },
  energy: {
    title: "Energy & mood",
    subtitle: "1 = sleepy, 5 = bouncing off the walls.",
    fields: [
      { key: "energy_level", label: "Energy", type: "scale", detail: false },
      { key: "mood", label: "Mood", type: "chips", options: ["calm", "happy", "anxious", "hyper", "lethargic"], detail: true },
      { key: "sleep", label: "Sleep pattern", type: "chips", options: ["slept well", "restless", "extra naps"], detail: true },
    ],
  },
  weight: {
    title: "Weight check",
    subtitle: "Tracked on the stats panel and the vet PDF.",
    fields: [
      { key: "weight_lbs", label: "Weight (lbs)", type: "number", step: "0.1", detail: false },
      { key: "body_condition", label: "Body condition (1=under, 5=over)", type: "scale", detail: true },
    ],
  },
  meds: {
    title: "Medication given",
    subtitle: "Track what, how much, and when.",
    fields: [
      { key: "med_name", label: "Medication", type: "text", detail: true, placeholder: "Apoquel, Heartgard, ..." },
      { key: "dosage", label: "Dose", type: "text", detail: true, placeholder: "5mg, 1 tablet, ..." },
      { key: "route", label: "Route", type: "chips", options: ["oral", "topical", "injection", "ear", "eye"], detail: true },
      { key: "with_food", label: "With food", type: "toggle", detail: true },
    ],
  },
  symptom: {
    title: "Symptom log",
    subtitle: "Two days at severity 3+ triggers a vet warning.",
    fields: [
      { key: "what", label: "What is happening", type: "text", detail: true, placeholder: "limping, vomit, scratching..." },
      { key: "area", label: "Body area", type: "text", detail: true, placeholder: "front-right paw, belly, ..." },
      { key: "severity", label: "Severity (1=mild, 5=severe)", type: "scale", detail: false },
      { key: "started_when", label: "Started", type: "chips", options: ["just now", "today", "yesterday", "this week", "longer"], detail: true },
    ],
  },
  training: {
    title: "Training milestone",
    subtitle: "Build the resume — vets, sitters and buyers love this.",
    fields: [
      { key: "task", label: "Skill / task", type: "text", detail: true, placeholder: "sit, recall, loose leash..." },
      { key: "duration_min", label: "Duration (min)", type: "number", detail: false },
      { key: "outcome", label: "How'd it go", type: "chips", options: ["nailed it", "made progress", "struggled"], detail: true },
      { key: "reward", label: "Reward used", type: "chips", options: ["treat", "praise", "toy", "play"], detail: true },
    ],
  },
  grooming: {
    title: "Grooming",
    subtitle: "Bath, brush, nails — keep the cadence.",
    fields: [
      { key: "type", label: "Type", type: "chips", options: ["bath", "brushing", "nail trim", "teeth", "ear cleaning", "professional"], detail: true },
      { key: "who", label: "Who did it", type: "chips", options: ["me", "groomer", "vet"], detail: true },
      { key: "products", label: "Products / shampoo", type: "text", detail: true },
    ],
  },
};

function LogDetailDialog({ kind, dogId, onClose, onSaved }) {
  const cfg = kind ? KIND_FIELDS[kind] : null;
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset on open/close.
  useEffect(() => {
    if (kind) {
      setValues({});
      setNotes("");
      setPhotoUrl(null);
    }
  }, [kind]);

  const set = (k, v) => setValues((cur) => ({ ...cur, [k]: v }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // /upload returns { storage_path, thumb_path, … } — store the raw
      // path; `thumbUrl()` resolves it to a fetchable URL at render time.
      const path = r.data.storage_path;
      if (path) setPhotoUrl(path);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPhotoBusy(false);
    }
  };

  const submit = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      // Split values into top-level columns vs `details` JSON based on
      // each field's `detail` flag in KIND_FIELDS.
      const payload = { dog_id: dogId, kind, notes: notes || null };
      const details = {};
      for (const f of cfg.fields) {
        const v = values[f.key];
        if (v === undefined || v === "" || v === null) continue;
        const out = f.type === "number" ? parseFloat(v) : v;
        if (f.detail) details[f.key] = out;
        else payload[f.key] = out;
      }
      if (Object.keys(details).length) payload.details = details;
      if (photoUrl) payload.photo_url = photoUrl;
      await api.post("/care/log", payload);
      toast.success(`${KIND_META[kind].label} logged`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;
  // Two-column grid for short "half"-width fields. Walk + Play use this.
  const halfFields = cfg.fields.filter((f) => f.half);
  const fullFields = cfg.fields.filter((f) => !f.half);

  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid={`care-detail-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl flex items-center gap-2">
            {React.createElement(KIND_META[kind].icon, { size: 20, weight: "duotone", style: { color: KIND_META[kind].color } })}
            {cfg.title}
          </DialogTitle>
          <DialogDescription>{cfg.subtitle}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {halfFields.length === 2 && (
            <div className="grid grid-cols-2 gap-3">
              {halfFields.map((f) => <FieldRenderer key={f.key} field={f} value={values[f.key]} onChange={(v) => set(f.key, v)} testId={`care-field-${kind}-${f.key}`} />)}
            </div>
          )}
          {fullFields.map((f) => (
            <FieldRenderer key={f.key} field={f} value={values[f.key]} onChange={(v) => set(f.key, v)} testId={`care-field-${kind}-${f.key}`} />
          ))}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth remembering..." data-testid={`care-field-${kind}-notes`} />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Camera size={12} weight="duotone" /> Photo (optional)
            </Label>
            <div className="mt-1 flex items-center gap-3">
              {photoUrl ? (
                <>
                  <img src={thumbUrl(photoUrl) || photoUrl} alt="" className="w-14 h-14 object-cover rounded" />
                  <Button variant="outline" size="sm" onClick={() => setPhotoUrl(null)} className="rounded-full text-xs">Remove</Button>
                </>
              ) : (
                <label className="cursor-pointer flex items-center gap-2 text-xs text-ink hover:text-crimson">
                  <input type="file" accept="image/*" onChange={(e) => uploadPhoto(e.target.files?.[0])} className="hidden" data-testid={`care-field-${kind}-photo`} />
                  <span className="px-3 py-1.5 rounded-full border border-[#EAEAEA] hover:border-ink">
                    {photoBusy ? "Uploading…" : "Add photo"}
                  </span>
                </label>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid={`care-detail-save-${kind}`}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Renders a single field according to its `type`. Generic so we don't
 *  duplicate JSX across 10 kinds — config-driven. */
function FieldRenderer({ field, value, onChange, testId }) {
  if (field.type === "chips") {
    return (
      <div>
        <Label className="text-xs">{field.label}</Label>
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid={testId}>
          {field.options.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => onChange(value === o ? null : o)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors capitalize ${
                value === o
                  ? "border-ink bg-ink text-white"
                  : "border-[#EAEAEA] text-[#3F3F3F] hover:border-ink"
              }`}
            >
              {o}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "scale") {
    return (
      <div>
        <Label className="text-xs">{field.label}</Label>
        <div className="mt-1 flex justify-between gap-2" data-testid={testId}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(value === n ? null : n)}
              className={`h-10 w-10 rounded-full text-base font-bold transition-colors ${
                value === n ? "bg-ink text-white" : "bg-[#FAFAFA] border border-[#EAEAEA] hover:border-ink"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "toggle") {
    return (
      <button
        type="button"
        onClick={() => onChange(!value)}
        className="w-full flex items-center justify-between p-2.5 rounded-lg border border-[#EAEAEA] hover:border-ink transition-colors"
        data-testid={testId}
      >
        <span className="text-sm font-medium">{field.label}</span>
        <span className={`relative h-5 w-9 rounded-full transition-colors flex-shrink-0 ${value ? "bg-ink" : "bg-[#E5E5E5]"}`}>
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${value ? "left-[18px]" : "left-0.5"}`} />
        </span>
      </button>
    );
  }
  // number / text
  return (
    <div>
      <Label className="text-xs">{field.label}</Label>
      <Input
        type={field.type}
        step={field.step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        data-testid={testId}
      />
    </div>
  );
}

// ----------------------------------------------------------------------
// Stats panel
// ----------------------------------------------------------------------
function StatsPanel({ stats }) {
  if (!stats) return null;
  const tiles = [
    { label: "Walk streak", value: stats.walk_streak_days, unit: stats.walk_streak_days === 1 ? "day" : "days", icon: Heart, tone: "#C1272D" },
    { label: "Walks (7d)", value: stats.walks_last_7d, unit: "", icon: PawPrint, tone: "#3F4A8A" },
    { label: "Avg distance", value: stats.avg_distance_per_walk_7d, unit: "mi", icon: Sparkle, tone: "#C9A227" },
    { label: "Energy (avg)", value: stats.avg_energy_30d ?? "—", unit: stats.avg_energy_30d ? "/ 5" : "", icon: Lightning, tone: "#8A6A00" },
    { label: "Weight", value: stats.latest_weight_lbs ?? "—", unit: stats.latest_weight_lbs ? "lbs" : "", icon: Scales, tone: "#0E0E0E", trend: stats.weight_trend_30d_lbs },
  ];
  return (
    <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="care-stats">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-xl border border-[#EAEAEA] bg-white p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded" style={{ background: `${t.tone}15` }}>
              <t.icon size={14} weight="duotone" color={t.tone} />
            </div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">{t.label}</span>
          </div>
          <div className="text-2xl font-spectral font-light tracking-tight font-tabular leading-none">
            {t.value}<span className="text-sm text-[#9A9A9A] ml-1">{t.unit}</span>
          </div>
          {t.trend !== null && t.trend !== undefined && (
            <div className={`text-[10px] mt-1.5 font-bold ${t.trend > 0 ? "text-[#C1272D]" : t.trend < 0 ? "text-[#0F8043]" : "text-[#9A9A9A]"}`}>
              {t.trend > 0 ? "+" : ""}{t.trend} lbs · 30d
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

// ----------------------------------------------------------------------
// Appointments
// ----------------------------------------------------------------------
function AppointmentsPanel({ dogId, appointments, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="care-appointments">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
          <CalendarBlank size={18} weight="duotone" className="text-ink" />
          Upcoming appointments
        </h3>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-ink hover:bg-crimson text-white text-xs h-8" data-testid="care-appt-add">
          <Plus size={12} weight="bold" className="mr-1" /> Add
        </Button>
      </div>
      {appointments.length === 0 && (
        <p className="text-sm text-[#6B6B6B]" data-testid="care-appt-empty">No upcoming appointments.</p>
      )}
      <div className="space-y-2">
        {appointments.map((a) => (
          <AppointmentRow key={a.id} appt={a} onChange={onChange} />
        ))}
      </div>
      <AppointmentDialog open={open} onOpenChange={setOpen} dogId={dogId} onSaved={onChange} />
    </section>
  );
}

function AppointmentRow({ appt, onChange }) {
  const remove = async () => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await api.delete(`/care/appointments/${appt.id}`);
      toast.success("Appointment deleted");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const description = [
    appt.notes,
    appt.contact_name ? `Contact: ${appt.contact_name}` : "",
    appt.contact_phone ? `Phone: ${appt.contact_phone}` : "",
  ].filter(Boolean).join("\n");
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-[#EAEAEA] hover:border-ink transition-colors" data-testid={`care-appt-${appt.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">{appt.title}</p>
        <p className="text-[12px] text-[#6B6B6B] truncate">
          {appt.kind.toUpperCase()} · {formatDate(appt.starts_at)} · {appt.starts_at.slice(11, 16)}
          {appt.location ? ` · ${appt.location}` : ""}
        </p>
      </div>
      <AddToCalendarMenu
        title={appt.title}
        startsAt={appt.starts_at}
        durationMin={appt.duration_min || 30}
        location={appt.location || ""}
        description={description}
        icsUrl={`/care/appointments/${appt.id}/ics`}
        icsRequiresAuth
        icsFilename={`appt-${appt.id}.ics`}
        testId={`care-appt-${appt.id}-cal`}
      />
      <button onClick={remove} className="text-[#9A9A9A] hover:text-crimson p-1" data-testid={`care-appt-${appt.id}-del`}>
        <Trash size={14} />
      </button>
    </div>
  );
}

function AppointmentDialog({ open, onOpenChange, dogId, onSaved }) {
  const [kind, setKind] = useState("vet");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState("30");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const submit = async () => {
    if (!title || !date) {
      toast.error("Title and date are required");
      return;
    }
    try {
      const starts_at = new Date(`${date}T${time}:00Z`).toISOString();
      await api.post("/care/appointments", {
        dog_id: dogId,
        kind, title, starts_at,
        duration_min: parseInt(duration, 10) || 30,
        location: location || null,
        notes: notes || null,
      });
      toast.success("Appointment added");
      setTitle(""); setDate(""); setTime("10:00"); setLocation(""); setNotes(""); setKind("vet");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="care-appt-dialog">
        <DialogHeader>
          <DialogTitle>New appointment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="care-appt-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vet">Vet</SelectItem>
                <SelectItem value="groomer">Groomer</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="boarding">Boarding</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Annual checkup" data-testid="care-appt-title" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Date</Label>
              <DateOfBirthInput value={date} onChange={setDate} testId="care-appt-date" autoComplete="off" ariaLabel="Appointment date" />
            </div>
            <div>
              <Label>Time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="care-appt-save">Save appointment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------
// Sitter share links
// ----------------------------------------------------------------------
function SitterLinksPanel({ dogId, dog, links, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="care-sitter-links">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
          <Share size={18} weight="duotone" className="text-ink" />
          Pet sitter access
        </h3>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-ink hover:bg-crimson text-white text-xs h-8" data-testid="care-sitter-add">
          <Plus size={12} weight="bold" className="mr-1" /> New share link
        </Button>
      </div>
      {links.length === 0 && (
        <p className="text-sm text-[#6B6B6B]" data-testid="care-sitter-empty">
          Generate a share link to give a pet sitter temporary access — they can log activities live without an account.
        </p>
      )}
      <div className="space-y-2">
        {links.map((l) => (
          <SitterLinkRow key={l.id} link={l} onChange={onChange} />
        ))}
      </div>
      <SitterLinkDialog open={open} onOpenChange={setOpen} dogId={dogId} dog={dog} onSaved={onChange} />
    </section>
  );
}

function SitterLinkRow({ link, onChange }) {
  const url = `${webOrigin()}/sitter/${link.token}`;
  const today = new Date().toISOString().slice(0, 10);
  const active = !link.revoked_at && today >= link.starts_on && today <= link.ends_on;
  const [emailing, setEmailing] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const revoke = async () => {
    if (!window.confirm("Revoke this sitter link? They won't be able to log in or post anymore.")) return;
    try {
      await api.delete(`/care/sitter-links/${link.id}`);
      toast.success("Link revoked");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Web Share API — opens the phone's native share sheet on
  // iOS/Android (Messages, WhatsApp, Mail, AirDrop, etc.). On desktop
  // Chrome it falls back to copy.
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Sitter link for ${link.sitter_name || "your sitter"}`,
          text: `You're sitting from ${link.starts_on} to ${link.ends_on}. Open this link on your phone:`,
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Copied — your browser doesn't support native sharing.");
    }
  };

  const emailInvite = async () => {
    if (!link.sitter_email) {
      toast.error("Add a sitter email when creating the link.");
      return;
    }
    setEmailing(true);
    try {
      await api.post(`/care/sitter-links/${link.id}/email-invite`, { public_url: url });
      toast.success(`Invite sent to ${link.sitter_email}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#EAEAEA] p-3" data-testid={`care-sitter-${link.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{link.sitter_name}</p>
          {link.sitter_email && (
            <p className="text-[11px] text-[#9A9A9A] truncate">{link.sitter_email}</p>
          )}
        </div>
        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 flex-shrink-0 ${
          link.revoked_at ? "bg-[#FAFAFA] text-[#9A9A9A]" :
          active ? "bg-[#0F8043]/10 text-[#0F8043]" :
          "bg-gold/15 text-[#8A6A00]"
        }`}>
          {link.revoked_at ? "Revoked" : active ? "Active" : "Scheduled"}
        </span>
      </div>
      <p className="text-[12px] text-[#6B6B6B]">{link.starts_on} → {link.ends_on}</p>
      {!link.revoked_at && (
        <>
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            <Button onClick={nativeShare} className="rounded-full bg-ink hover:bg-crimson text-white text-xs h-8" data-testid={`care-sitter-${link.id}-share`}>
              <Share size={12} weight="bold" className="mr-1" /> Share
            </Button>
            <Button
              onClick={emailInvite}
              disabled={!link.sitter_email || emailing}
              variant="outline"
              className="rounded-full text-xs h-8"
              data-testid={`care-sitter-${link.id}-email`}
              title={link.sitter_email ? `Email to ${link.sitter_email}` : "Add a sitter email to enable"}
            >
              <PaperPlaneTilt size={12} weight="bold" className="mr-1" /> {emailing ? "Sending…" : "Email link"}
            </Button>
            <Button onClick={() => setQrOpen(true)} variant="outline" className="rounded-full text-xs h-8" data-testid={`care-sitter-${link.id}-qr`}>
              <QrCode size={12} weight="bold" className="mr-1" /> QR code
            </Button>
            <Button
              onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied"); }}
              variant="outline"
              className="rounded-full text-xs h-8"
              data-testid={`care-sitter-${link.id}-copy`}
            >
              <Copy size={12} weight="bold" className="mr-1" /> Copy
            </Button>
            <button onClick={revoke} className="ml-auto text-[#9A9A9A] hover:text-crimson p-1" data-testid={`care-sitter-${link.id}-revoke`}>
              <Trash size={14} />
            </button>
          </div>
          <details className="mt-2">
            <summary className="text-[11px] text-[#9A9A9A] cursor-pointer hover:text-ink">Reveal URL</summary>
            <Input value={url} readOnly className="text-xs h-8 font-mono mt-1.5" data-testid={`care-sitter-${link.id}-url`} />
          </details>
        </>
      )}
      <SitterQrDialog open={qrOpen} onOpenChange={setQrOpen} url={url} sitterName={link.sitter_name} />
    </div>
  );
}

/** Shows a Google-Charts-rendered QR code so the sitter can scan from
 *  the owner's phone in-person. No client-side QR lib needed — the
 *  Google endpoint is free, public, and returns a PNG. */
function SitterQrDialog({ open, onOpenChange, url, sitterName }) {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=${encodeURIComponent(url)}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="care-sitter-qr-dialog">
        <DialogHeader>
          <DialogTitle>Share with {sitterName || "your sitter"}</DialogTitle>
          <DialogDescription>
            Hand your phone to your sitter — they scan with their camera and the link opens instantly. No accounts, no typing.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center py-2">
          <img src={qrSrc} alt="Sitter QR code" width={320} height={320} className="rounded-lg border border-[#EAEAEA]" />
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="rounded-full bg-ink hover:bg-crimson text-white w-full">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SitterLinkDialog({ open, onOpenChange, dogId, onSaved }) {
  const [form, setForm] = useState({
    sitter_name: "", sitter_email: "",
    starts_on: new Date().toISOString().slice(0, 10),
    ends_on: new Date(Date.now() + 7 * 86400 * 1000).toISOString().slice(0, 10),
    owner_notes: "", feeding_instructions: "", meds: "",
    vet_phone: "", emergency_contact: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.sitter_name) {
      toast.error("Sitter name is required");
      return;
    }
    try {
      await api.post("/care/sitter-links", { dog_id: dogId, ...form });
      toast.success("Share link created — copy it to send to your sitter");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="care-sitter-dialog">
        <DialogHeader>
          <DialogTitle>New sitter share link</DialogTitle>
          <DialogDescription>
            The sitter opens the URL on their phone — no signup. Their log entries flow back into your timeline.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sitter name</Label>
              <Input value={form.sitter_name} onChange={(e) => set("sitter_name", e.target.value)} data-testid="care-sitter-name" />
            </div>
            <div>
              <Label>Sitter email (optional)</Label>
              <Input type="email" value={form.sitter_email} onChange={(e) => set("sitter_email", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <DateOfBirthInput value={form.starts_on} onChange={(v) => set("starts_on", v)} testId="care-sitter-start" autoComplete="off" ariaLabel="Sitter start date" />
            </div>
            <div>
              <Label>End date</Label>
              <DateOfBirthInput value={form.ends_on} onChange={(v) => set("ends_on", v)} testId="care-sitter-end" autoComplete="off" ariaLabel="Sitter end date" />
            </div>
          </div>
          <div>
            <Label>Feeding instructions</Label>
            <Textarea rows={2} value={form.feeding_instructions} onChange={(e) => set("feeding_instructions", e.target.value)} placeholder="2 cups Acana, 7am + 6pm" />
          </div>
          <div>
            <Label>Medications</Label>
            <Textarea rows={2} value={form.meds} onChange={(e) => set("meds", e.target.value)} placeholder="None / dosage details" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Vet phone</Label>
              <Input value={form.vet_phone} onChange={(e) => set("vet_phone", e.target.value)} />
            </div>
            <div>
              <Label>Emergency contact</Label>
              <Input value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Other owner notes</Label>
            <Textarea rows={2} value={form.owner_notes} onChange={(e) => set("owner_notes", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="care-sitter-save">Create share link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------
// PDF reports
// ----------------------------------------------------------------------
function ReportsPanel({ dogId, sitterLinks }) {
  const [busy, setBusy] = useState(null);
  const download = async (key, path) => {
    setBusy(key);
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${key}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };
  // For "Return Report" we need a sitter link id — show most recent one
  // whose window has ended (or any non-revoked one if none have ended).
  const today = new Date().toISOString().slice(0, 10);
  const returnLink =
    sitterLinks.find((l) => !l.revoked_at && l.ends_on < today) ||
    sitterLinks.find((l) => !l.revoked_at);

  return (
    <section className="rounded-xl border border-[#EAEAEA] bg-white p-5" data-testid="care-reports">
      <h3 className="font-spectral text-2xl tracking-tight flex items-center gap-2 mb-3">
        <FilePdf size={18} weight="duotone" className="text-ink" />
        Downloadable reports
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ReportCard
          title="Vet Visit Report"
          desc="Last 30 days of meals, walks, energy, appointments — ready for your vet."
          onClick={() => download("vet-visit", `/care/reports/vet-visit.pdf?dog_id=${dogId}`)}
          busy={busy === "vet-visit"}
          testId="care-report-vet"
        />
        <ReportCard
          title="Pet Sitter Briefing"
          desc="Routine, feeding, meds, vet contact, emergency info. Hand to your sitter before they start."
          onClick={() => download("sitter-briefing", `/care/reports/sitter-briefing.pdf?dog_id=${dogId}${returnLink ? `&sitter_link_id=${returnLink.id}` : ""}`)}
          busy={busy === "sitter-briefing"}
          testId="care-report-briefing"
        />
        <ReportCard
          title="Sitter Return Report"
          desc={returnLink ? "Everything the sitter logged during their stay." : "Create a sitter link first; this report fills in once they start logging."}
          onClick={() => returnLink && download("sitter-return", `/care/reports/sitter-return.pdf?sitter_link_id=${returnLink.id}`)}
          disabled={!returnLink}
          busy={busy === "sitter-return"}
          testId="care-report-return"
        />
      </div>
    </section>
  );
}

function ReportCard({ title, desc, onClick, busy, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      data-testid={testId}
      className="text-left p-4 rounded-lg border border-[#EAEAEA] hover:border-ink hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
    >
      <p className="font-semibold text-sm mb-1">{title}</p>
      <p className="text-[12px] text-[#6B6B6B] leading-snug">{desc}</p>
      <p className="text-[11px] font-bold text-crimson mt-2">
        {busy ? "Generating…" : disabled ? "Not available yet" : "Download PDF →"}
      </p>
    </button>
  );
}


/** Lightweight Saturday-recap email toggle. Persisted on the user
 *  document, so the preference applies across every dog they own. */
function RecapPreferencesCard() {
  const [optOut, setOptOut] = useState(null);
  useEffect(() => {
    api.get("/care/recap-preferences").then((r) => setOptOut(!!r.data?.opt_out)).catch(() => {});
  }, []);
  if (optOut === null) return null;
  const toggle = async () => {
    const next = !optOut;
    setOptOut(next);
    try {
      await api.post("/care/recap-preferences", { opt_out: next });
      toast.success(next ? "Saturday recap turned off" : "Saturday recap turned on");
    } catch (err) {
      setOptOut(!next);
      toast.error(formatApiError(err));
    }
  };
  return (
    <section
      className="rounded-xl border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-4 flex items-center justify-between gap-4"
      data-testid="care-recap-preferences"
    >
      <div className="flex-1">
        <p className="text-sm font-semibold flex items-center gap-2">
          Saturday weekly recap
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-gold bg-gold/10 rounded-full px-1.5 py-0.5">Email</span>
        </p>
        <p className="text-[12px] text-[#6B6B6B] mt-0.5 leading-snug">
          A short weekly summary of your dog's walks, meals, streaks, and headlines —
          delivered Saturday morning. Skipped automatically if there's no activity.
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!optOut}
        onClick={toggle}
        data-testid="care-recap-toggle"
        className={`relative h-6 w-11 rounded-full transition-colors flex-shrink-0 ${
          optOut ? "bg-[#E5E5E5]" : "bg-ink"
        }`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
          optOut ? "left-0.5" : "left-[22px]"
        }`} />
      </button>
    </section>
  );
}

